import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  NotificationsNone as NotificationsIcon,
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  WarningAmberOutlined as WarningIcon,
  InfoOutlined as InfoIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { useNotifications } from '../context/NotificationContext';
import type { AppNotification } from '../services/types';

const severityIcon: Record<string, React.ReactElement> = {
  success: <SuccessIcon fontSize="small" color="success" />,
  error: <ErrorIcon fontSize="small" color="error" />,
  warning: <WarningIcon fontSize="small" color="warning" />,
  info: <InfoIcon fontSize="small" color="info" />,
};

function timeAgo(dateStr: string | undefined): string {
  if (!dateStr) return 'just now';
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return 'just now';
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

const NotificationPanel: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead } =
    useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (n: AppNotification) => {
    if (!n.is_read) markRead(n.notification_id);

    // Resolve the batch_id to navigate to.
    // For batch entities, entity_id IS the batch_id.
    // For execution_run / recipe entities, batch_id is in metadata.
    const batchId =
      n.entity_type === 'batch'
        ? n.entity_id
        : n.metadata?.batch_id || n.entity_id;

    if (batchId) {
      navigate(`/batches/${batchId}`);
    }

    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton size="small" color="inherit" onClick={handleOpen}>
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 10,
              height: 18,
              minWidth: 18,
            },
          }}
        >
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 480,
            mt: 1,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={() => markAllRead()}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {/* List */}
        {notifications.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <NotificationsIcon
              sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ overflow: 'auto', maxHeight: 380 }}>
            {notifications.map((n) => (
              <ListItemButton
                key={n.notification_id}
                onClick={() => handleClick(n)}
                sx={{
                  py: 1.5,
                  px: 2,
                  bgcolor: n.is_read
                    ? 'transparent'
                    : alpha(theme.palette.primary.main, 0.04),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {severityIcon[n.severity] || severityIcon.info}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      fontWeight={n.is_read ? 400 : 600}
                      noWrap
                    >
                      {n.title}
                    </Typography>
                  }
                  secondary={
                    <Box
                      component="span"
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ flex: 1, mr: 1 }}
                      >
                        {n.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {timeAgo(n.created_at)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationPanel;
